import axios from "axios";
import { clearAuthTokens } from "./tokenUtils";
import { Toast, FORBIDDEN_MESSAGE } from "./toast";

// Endpoints where a 401 means "no access to this resource" not "session expired" — don't logout.
// Auth endpoints are included so a failed login surfaces its error to the caller
// instead of triggering a logout-redirect and a never-resolving promise (which
// leaves the login button spinning forever with no error shown).
const SKIP_LOGOUT_ON_401 = [
  "tutor/club/settings",
  "tutor/login",
  "tutor/ssologin",
  "tutor/handoff",
];

// Attach the stored auth token to every request. Callers that set the header
// explicitly are left untouched — the refresh-token call passes its refresh
// token. An explicit "" is treated as "set", so it is preserved.
axios.interceptors.request.use((config) => {
  if (config.headers && config.headers.authorization !== undefined) {
    return config;
  }
  const token = localStorage.getItem("TOKEN");
  if (token) {
    config.headers.authorization = token;
  }
  return config;
});

// 401 = token expired/invalid → logout. 403 = permission denied for a specific resource → don't logout.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const url = err.config?.url || "";
      if (err.config?.skipLogoutOn401 || SKIP_LOGOUT_ON_401.some((ep) => url.includes(ep))) {
        return Promise.reject(err);
      }
      clearAuthTokens();
      window.location.replace("/auth/login");
      err._isAuthError = true;
      return new Promise(() => {});
    }
    // The backend refused this caller. Say so once, in plain words, and give
    // callers that show e.response.data.message the same text.
    if (err.response?.status === 403) {
      if (err.response.data && typeof err.response.data === "object") {
        err.response.data.message = FORBIDDEN_MESSAGE;
      }
      err._isForbidden = true;
      Toast("error", FORBIDDEN_MESSAGE);
    }
    return Promise.reject(err);
  }
);

export const getQuery = (endpoint, header) => axios.get(endpoint, header);
export const patchQuery = (endpoint, data) => axios.patch(endpoint, data);
export const putQuery = (endpoint, data) => axios.put(endpoint, data);
export const postQuery = (endpoint, data, header) =>
  axios.post(endpoint, data, header);
// Some delete endpoints identify the record from the body rather than the URL.
export const deleteQuery = (endpoint, data) =>
  axios.delete(endpoint, data ? { data } : undefined);
